import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';

// Where the postcodes of ONE pallet row are: grid cells from the coverage
// endpoint, teal where a postcode lands on the row, grey where it bypasses it
// (island rows, a Geodis tier, another lane). Plain equirectangular, longitude
// squeezed by cos(latitude) so a country keeps its shape. GeoNames CC-BY: the
// attribution travels with the data and is shown under the map.
export const LaneCoverageMap = ({ cells, precision = 0.15, attribution }) => {
  const lands = cells?.lands || [];
  const bypass = cells?.bypass || [];
  const all = [...lands, ...bypass];
  if (!all.length) return null;
  const lats = all.map(c => c[0]);
  const lons = all.map(c => c[1]);
  const la0 = Math.min(...lats) - precision, la1 = Math.max(...lats) + precision;
  const lo0 = Math.min(...lons) - precision, lo1 = Math.max(...lons) + precision;
  const kx = Math.cos(((la0 + la1) / 2) * Math.PI / 180);
  const spanX = (lo1 - lo0) * kx, spanY = la1 - la0;
  const W = 420;
  const H = Math.max(200, Math.min(460, W * spanY / (spanX || 1)));
  const sc = Math.min(W / (spanX || 1), H / (spanY || 1));
  const X = lo => (lo - lo0) * kx * sc;
  const Y = la => (la1 - la) * sc;
  const sideX = Math.max(1.2, precision * sc * kx);
  const sideY = Math.max(1.2, precision * sc);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full rounded-md border border-border bg-muted/20" role="img"
           aria-label="Postcodes that land on this row (teal) and that bypass it (grey)">
        {bypass.map(([la, lo, n], i) => (
          <rect key={`b${i}`} x={X(lo) - sideX / 2} y={Y(la) - sideY / 2} width={sideX} height={sideY}
                fill="#94a3b8" fillOpacity="0.45"><title>{n} postcode{n === 1 ? '' : 's'} bypass this row</title></rect>
        ))}
        {lands.map(([la, lo, n], i) => (
          <rect key={`l${i}`} x={X(lo) - sideX / 2} y={Y(la) - sideY / 2} width={sideX} height={sideY}
                fill="#0d9488" fillOpacity="0.85"><title>{n} postcode{n === 1 ? '' : 's'} land on this row</title></rect>
        ))}
      </svg>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-1 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#0d9488]" /> lands on this row</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#94a3b8]" /> bypasses it</span>
        </span>
        <span>{attribution}</span>
      </div>
    </div>
  );
};

const LAND_COLORS = ['#0d9488', '#2563eb', '#16a34a', '#0891b2', '#65a30d', '#0284c7'];
const BYPASS_COLORS = ['#ea580c', '#a16207', '#9333ea', '#db2777', '#dc2626', '#64748b'];

const GroupTable = ({ rows, withReason, fmt }) => (
  <table className="w-full text-xs">
    <tbody>
      {rows.map((g, i) => (
        <tr key={i} className="border-b border-border/60">
          <td className="py-1 pr-2">
            {g.group}
            <div className="text-[10px] text-muted-foreground">
              {withReason ? `${g.reason} · ` : ''}e.g. {g.samples.join(', ')}
            </div>
          </td>
          <td className="py-1 text-right tabular-nums whitespace-nowrap">{fmt(g.count)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// What ONE pallet rate row covers: which postal zones land on it (counted in
// real postcodes), which bypass it and why, a map of both, and per zone the
// price this row asks against what that zone actually pays. Opened by
// clicking a row of the Pallet Rates tab (Volumetric or Real weight view).
// `Modal` and `RateLineChart` are the page's own, passed in so the panel
// looks like the rest of the Shipping Rates screen.
export const PalletLaneCoveragePanel = ({ laneKey, onClose, Modal, RateLineChart }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [pallet, setPallet] = useState('');
  const [xMode, setXMode] = useState('real');

  useEffect(() => { setPallet(''); setData(null); setErr(null); }, [laneKey]);
  useEffect(() => {
    if (!laneKey) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axiosInstance.get('api/shipping-rates/pallet-bands/coverage/', { params: { key: laneKey, pallet: pallet || undefined } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.error || 'Failed to load this row'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [laneKey, pallet]);

  if (!laneKey) return null;
  const fmt = n => (n == null ? '–' : Number(n).toLocaleString());
  const landsTotal = (data?.lands || []).reduce((a, g) => a + (g.count || 0), 0);
  const bypassTotal = (data?.bypass || []).reduce((a, g) => a + (g.count || 0), 0);
  let li = 0;
  let bi = 0;
  const series = (data?.series || []).map(sr => {
    const color = sr.kind === 'row' ? 'currentColor'
      : sr.kind === 'lands' ? LAND_COLORS[li++ % LAND_COLORS.length]
        : BYPASS_COLORS[bi++ % BYPASS_COLORS.length];
    return {
      label: sr.kind === 'row' ? sr.label : `${sr.label} (pays)`,
      color,
      dash: sr.kind === 'row' ? '6 4' : (sr.kind === 'bypass' ? '2 3' : undefined),
      width: sr.kind === 'row' ? 2.5 : 1.75,
      data: sr.data.map(d => ({ x: xMode === 'real' ? d.w : d.x, y: d.y })),
    };
  });

  return (
    <Modal open={!!laneKey} onClose={onClose} size="5xl"
           title={data ? `${data.country} · ${data.carrier} · ${data.lane || 'lane'}` : 'Pallet rate row'}>
      {loading && !data ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Resolving every postcode of this country...</div>
      ) : err ? (
        <div className="text-sm text-destructive">{err}</div>
      ) : data && (
        <>
          <p className="text-xs text-muted-foreground">
            Every real postcode of {data.country} is resolved the way the engine prices a pallet: an island zone with its
            own pallet rows keeps them, a Geodis tier prices French pallets on real weight, otherwise the carrier picks
            its lane.
            {data.list_only
              ? ' There is no postcode data for this country (GeoNames covers neither Greece, Ireland, the UK nor Belgium), so the row is tested on probe postcodes instead (one per postal zone, one per postcode token of the carrier\'s lanes), without counts or a map.'
              : ` ${fmt(data.points_total)} postcodes${data.sampled ? ' (an even sample of the country)' : ''}: ${fmt(landsTotal)} land on this row, ${fmt(bypassTotal)} bypass it. Counts cover the postcodes GeoNames knows, which is not every code a carrier lists (Lithuania: none of DHL's remote codes are in it).`}
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3 min-w-0">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Lands on this row</h3>
                {(data.lands || []).length
                  ? <GroupTable rows={data.lands} fmt={fmt} />
                  : <p className="text-xs text-muted-foreground italic">No postcode resolves to this row.</p>}
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Bypasses this row</h3>
                {(data.bypass || []).length
                  ? <GroupTable rows={data.bypass} withReason fmt={fmt} />
                  : <p className="text-xs text-muted-foreground italic">Nothing bypasses it: the whole country lands here.</p>}
              </div>
            </div>
            <div className="min-w-0">
              {data.list_only
                ? <div className="text-xs text-muted-foreground italic py-8 text-center border border-dashed border-border rounded-md">No map: no postcode coordinates for {data.country}.</div>
                : <LaneCoverageMap cells={data.cells} precision={data.precision} attribution={data.attribution} />}
            </div>
          </div>

          <div className="border-t border-border/60 pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pallet price per zone (shipping incl. fuel)</h3>
              <div className="flex items-center gap-2 text-xs">
                <select value={pallet || data.pallet} onChange={e => setPallet(e.target.value)}
                        className="h-7 rounded-md border border-border bg-card px-2 text-xs">
                  {(data.pallets || []).map(p => (
                    <option key={p} value={p}>{p}{(data.refused_pallets || []).includes(p) ? ' (refused by this carrier)' : ''}</option>
                  ))}
                </select>
                <div className="inline-flex rounded-md border border-border overflow-hidden">
                  {[['real', 'Real weight'], ['chargeable', 'Chargeable kg']].map(([v, lab]) => (
                    <button key={v} type="button" onClick={() => setXMode(v)}
                            className={`px-2 h-7 ${xMode === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{lab}</button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mb-1">
              Dashed: what this row asks for a {data.pallet}. Solid: what the postcodes landing here actually pay (the
              cheapest pallet any carrier or zone gives them). Dotted: what the bypassing zones pay instead.
              {xMode === 'chargeable' && " Chargeable kg follows this carrier's rule (the pallet's volume, floor metres), so light pallets collapse onto the pallet's own volumetric weight."}
              {(data.refused_pallets || []).length > 0 && ` This carrier refuses ${data.refused_pallets.join(', ')} on this row.`}
            </p>
            {loading && <p className="text-[11px] text-muted-foreground">Updating...</p>}
            <RateLineChart series={series} xLabel={xMode === 'real' ? 'real weight of the load (kg)' : 'chargeable weight (kg)'} />
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {series.map((sr, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                  <svg width="18" height="6" aria-hidden>
                    <line x1="0" y1="3" x2="18" y2="3" stroke={sr.color === 'currentColor' ? '#6b7280' : sr.color}
                          strokeWidth="2" strokeDasharray={sr.dash || undefined} />
                  </svg>
                  {sr.label}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default PalletLaneCoveragePanel;
